import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { SortableTable, type SortableColumn } from '@/components/ui/SortableTable';
import { fetchSeoAlerts, resolveSeoAlert } from '@/data/sources-seod';
import type { SeoAlert, SeoAlertStatus } from '@/types/seod';
import { formatRelative } from '@/lib/utils';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { filterRowsByActiveDomain, scopeLabel } from '@/pages/seo/domain-scope';

/* /seo/alerts — SEO Alerts inbox (W4a). A read-only list of the workspace's
 * stored, evidence-backed alerts (bc_seo_alerts) with an owner/operator resolve
 * action wired to the EXISTING POST /v1/seo/alerts/{id}/resolve.
 *
 * HONESTY + SAFETY INVARIANTS (W4a contract):
 *  - FREE read on mount (stored alerts, no provider call, no charge).
 *  - Every alert's evidence (keyword + previous/current position) comes from the
 *    alert's own stored detail_json — rendered honestly, em-dash for anything
 *    the alert doesn't carry, never a fabricated position.
 *  - Resolve is the only write; owner/operator only (server-enforced too), with
 *    an honest optimistic → confirmed → error path and a list refetch.
 */

const STATUS_TABS: { key: SeoAlertStatus; label: string }[] = [
  { key: 'open', label: 'Open' },
  { key: 'acknowledged', label: 'Acknowledged' },
  { key: 'resolved', label: 'Resolved' },
];

function severityTone(sev: string): 'neutral' | 'warning' | 'danger' {
  const s = sev.toLowerCase();
  if (s === 'critical' || s === 'high' || s === 'error') return 'danger';
  if (s === 'warning' || s === 'medium') return 'warning';
  return 'neutral';
}

function movement(previous: number | null, current: number | null): string {
  const p = previous === null ? '—' : String(previous);
  const c = current === null ? '—' : String(current);
  if (previous === null && current === null) return '—';
  return `${p} → ${c}`;
}

export function SeoAlertsPage() {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const queryClient = useQueryClient();
  const { active, domains } = useActiveDomain();

  const [status, setStatus] = useState<SeoAlertStatus>('open');
  const [resolveError, setResolveError] = useState<string | null>(null);

  const alertsQ = useQuery({
    queryKey: ['seo', 'alerts', status],
    queryFn: () => fetchSeoAlerts(status, 100),
  });

  const resolveM = useMutation({
    mutationFn: (alertId: number) => resolveSeoAlert(alertId),
    onMutate: () => setResolveError(null),
    onSuccess: () => {
      // Refresh every alerts view + any executive/actions view that reads them.
      void queryClient.invalidateQueries({ queryKey: ['seo', 'alerts'] });
      void queryClient.invalidateQueries({ queryKey: ['seo', 'recommended-actions'] });
      void queryClient.invalidateQueries({ queryKey: ['seo', 'dashboard-summary'] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          setResolveError('Resolving alerts is limited to workspace owners and operators.');
        } else if (err.status === 404) {
          setResolveError('That alert is no longer available in this workspace.');
        } else {
          setResolveError(err.message || 'The alert could not be resolved — nothing changed.');
        }
      } else {
        setResolveError('The alert could not be resolved — nothing changed.');
      }
    },
  });

  const alerts = filterRowsByActiveDomain(alertsQ.data?.alerts ?? [], active);
  const resolvingId = resolveM.isPending ? resolveM.variables : null;

  const columns: SortableColumn<SeoAlert>[] = [
    {
      key: 'severity',
      header: 'Severity',
      sortable: true,
      sortValue: (a) => a.severity,
      render: (a) => <Badge tone={severityTone(a.severity)}>{a.severity || '—'}</Badge>,
      cellClassName: 'whitespace-nowrap',
    },
    {
      key: 'title',
      header: 'Alert',
      sortable: true,
      sortValue: (a) => a.title,
      render: (a) => a.title || '—',
      truncate: true,
      maxWidthClass: 'max-w-[280px]',
      title: (a) => a.title,
      cellClassName: 'font-medium text-text-primary',
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      sortValue: (a) => a.alert_type,
      render: (a) => <span className="font-mono text-xs text-text-tertiary">{a.alert_type}</span>,
      cellClassName: 'whitespace-nowrap',
    },
    {
      key: 'domain',
      header: 'Domain',
      render: (a) => {
        const d = domains.find((o) => o.projectId === a.project_id);
        return <span className="text-text-tertiary">{d ? d.hostname : '—'}</span>;
      },
      cellClassName: 'whitespace-nowrap',
    },
    {
      key: 'keyword',
      header: 'Keyword',
      render: (a) => a.detail.keyword || '—',
      truncate: true,
      maxWidthClass: 'max-w-[200px]',
      title: (a) => a.detail.keyword ?? undefined,
    },
    {
      key: 'movement',
      header: 'Position',
      align: 'right',
      render: (a) => (
        <span className="font-mono text-text-secondary">
          {movement(a.detail.previous, a.detail.current)}
        </span>
      ),
      cellClassName: 'whitespace-nowrap',
    },
    {
      key: 'created_at',
      header: 'Raised',
      sortable: true,
      sortValue: (a) => a.created_at,
      render: (a) => (a.created_at ? formatRelative(a.created_at) : '—'),
      cellClassName: 'whitespace-nowrap text-text-tertiary',
    },
    {
      key: 'resolve',
      header: '',
      align: 'right',
      render: (a) =>
        a.status === 'resolved' ? (
          <span className="text-xs text-text-tertiary">Resolved</span>
        ) : (
          <Button
            variant="secondary"
            onClick={() => resolveM.mutate(a.id)}
            disabled={!canManage || resolvingId === a.id}
            title={canManage ? undefined : 'Owner/operator only'}
          >
            {resolvingId === a.id ? 'Resolving…' : 'Resolve'}
          </Button>
        ),
      cellClassName: 'whitespace-nowrap',
    },
  ];

  const tabs = (
    <div className="flex items-center gap-1" role="tablist" aria-label="Alert status">
      {STATUS_TABS.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={status === t.key}
          onClick={() => setStatus(t.key)}
          className={
            status === t.key
              ? 'h-control rounded-md bg-inset px-3 text-sm font-medium text-text-primary'
              : 'h-control rounded-md px-3 text-sm text-text-secondary hover:bg-hover'
          }
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="SEO Alerts"
        subtitle="Evidence-backed alerts raised by the scheduler from your stored ranking history — a ranking drop, a top-10 exit or a new top-10 entry. Free to view; resolving is owner/operator only."
        actions={tabs}
      />

      {!canManage && (
        <div role="status" className="rounded-md border border-border-subtle bg-inset/50 px-3 py-2 text-sm text-text-secondary">
          You can review every alert here. Resolving them is limited to workspace owners and operators.
        </div>
      )}

      {resolveError && (
        <div role="alert" className="rounded-md border border-warning/30 bg-surface px-3 py-2 text-sm text-text-secondary">
          {resolveError}
        </div>
      )}

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="neutral">Source: bc_seo_alerts</Badge>
          <Badge tone="neutral">{scopeLabel(active)}</Badge>
          <span className="text-xs text-text-tertiary">
            Read-only from stored alerts · a missing position is shown as an em-dash, never a
            fabricated value
          </span>
        </div>

        <div className="mt-4">
          {alertsQ.isPending ? (
            <LoadingState rows={4} />
          ) : alertsQ.error ? (
            <ErrorState error={alertsQ.error} onRetry={() => void alertsQ.refetch()} />
          ) : alerts.length === 0 ? (
            <EmptyState
              title={`No ${status} alerts`}
              teach="Alerts appear here as the scheduler checks your tracked keywords and detects meaningful ranking changes. Nothing is invented in the meantime — an empty inbox means no changes crossed the alert thresholds."
            />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
              <SortableTable
                rows={alerts}
                columns={columns}
                getRowKey={(a) => `alert-${a.id}`}
                minWidthClass="min-w-[820px]"
                caption="Evidence-backed SEO alerts with an owner/operator resolve action"
              />
              <div className="border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
                {alerts.length} {status} {alerts.length === 1 ? 'alert' : 'alerts'} · read-only from
                bc_seo_alerts
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
